import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useInView, useReducedMotion } from "framer-motion";
import { useCountUp } from "@/hooks/useCountUp";
import { EVIDENCE_SOURCES, EVIDENCE_CONCLUSIONS } from "./homeData";

const SRC_Y = [58, 122, 186, 250];
const CX = 470;
const CY = 154;

export function EvidenceFlow() {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.35, once: false });
  const signals = useCountUp(214, inView, { duration: 1800, reduce });
  const [conc, setConc] = useState(0);

  useEffect(() => {
    if (reduce || !inView) return;
    const id = setInterval(() => setConc((p) => (p + 1) % EVIDENCE_CONCLUSIONS.length), 2200);
    return () => clearInterval(id);
  }, [inView, reduce]);

  return (
    <section ref={ref} className="relative overflow-hidden border-t border-donkey-line bg-donkey-deep">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="w-16 h-1 rounded-full bg-donkey-orange mb-6" />
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-donkey-warm">Researched honestly</h2>
          <p className="text-donkey-grey mt-4 leading-relaxed max-w-xl">
            The Donkey pulls real signals together, weighs them, and only keeps what stands up. You see the evidence, the weaknesses and the uncertainty.
          </p>
          <div className="mt-6 flex items-center gap-3">
            <span className="font-mono text-3xl text-donkey-orange" data-testid="evidence-signals">{signals}</span>
            <span className="text-sm text-donkey-grey">signals weighed in this pass</span>
          </div>
          <div className="mt-6 min-h-[3rem]">
            <p className="text-[11px] uppercase tracking-wider text-donkey-grey mb-1">Latest conclusion</p>
            <AnimatePresence mode="wait">
              <motion.p
                key={conc}
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -8 }}
                transition={{ duration: 0.4 }}
                className="font-heading font-semibold text-donkey-warm"
              >
                {EVIDENCE_CONCLUSIONS[conc]}
              </motion.p>
            </AnimatePresence>
          </div>
          <p className="mt-6 text-xs text-donkey-grey/70">Illustrative visualisation of the research step.</p>
        </div>

        <div className="relative">
          <svg viewBox="0 0 640 320" className="w-full h-auto" role="img" aria-label="Evidence sources flowing into the Donkey's analysis">
            {SRC_Y.map((y, i) => (
              <line key={`l${i}`} x1={170} y1={y} x2={CX} y2={CY} stroke="#333333" strokeWidth="1.5" />
            ))}
            {!reduce &&
              SRC_Y.map((y, i) =>
                [0, 1].map((k) => (
                  <motion.circle
                    key={`d${i}-${k}`}
                    r="4"
                    fill="#F97316"
                    initial={{ cx: 170, cy: y, opacity: 0 }}
                    animate={inView ? { cx: [170, CX], cy: [y, CY], opacity: [0, 1, 1, 0] } : {}}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeIn", delay: i * 0.5 + k * 1 }}
                  />
                ))
              )}
            {SRC_Y.map((y, i) => (
              <g key={`s${i}`}>
                <circle cx={170} cy={y} r="10" fill="#1F1F1F" stroke="#F97316" strokeOpacity="0.5" strokeWidth="1.5" />
                <text x={152} y={y + 4} textAnchor="end" fontSize="13" fill="#A8A29E" fontFamily="Inter, sans-serif">
                  {EVIDENCE_SOURCES[i]}
                </text>
              </g>
            ))}
            {/* analysis core */}
            <motion.circle
              cx={CX} cy={CY} r="46" fill="#111111" stroke="#F97316" strokeWidth="2"
              animate={reduce ? {} : { strokeOpacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.circle
              cx={CX} cy={CY} r="46" fill="none" stroke="#F97316" strokeWidth="1"
              style={{ transformOrigin: `${CX}px ${CY}px` }}
              animate={reduce ? {} : { scale: [1, 1.18, 1], strokeOpacity: [0.6, 0, 0.6] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            />
            <text x={CX} y={CY - 4} textAnchor="middle" fontSize="14" fill="#FFF7ED" fontFamily="Manrope, sans-serif" fontWeight="700">
              Analysis
            </text>
            <text x={CX} y={CY + 16} textAnchor="middle" fontSize="11" fill="#A8A29E" fontFamily="JetBrains Mono, monospace">
              weighing
            </text>
          </svg>
        </div>
      </div>
    </section>
  );
}

export default EvidenceFlow;
